// =====================================================
// RULE SIMULATOR
// Stage 2: Participants & Pricing
// "Probar reglas": which rule wins and what a person would pay today
// =====================================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { eventParticipantsRepository } from '@/data/eventParticipantsRepository';
import { PARTICIPANT_TYPE_OPTIONS } from '@/models/pricingRule';
import { formatPrice } from '@/models/event';
import { MinisterSearchCombobox } from './MinisterSearchCombobox';
import type { ParticipantType } from '@/types/pricing.types';

interface RuleSimulatorProps {
  eventId: string;
  basePrice: number;
  /** changes whenever rules change, so the result refreshes */
  rulesSignature: string;
}

const formatBs = formatPrice;

export function RuleSimulator({ eventId, basePrice, rulesSignature }: RuleSimulatorProps) {
  const [type, setType] = useState<ParticipantType>('MINISTER');
  const [ministerId, setMinisterId] = useState('');

  const ready = type !== 'MINISTER' || !!ministerId;
  const { data: result, isFetching } = useQuery({
    queryKey: ['ruleSimulation', eventId, type, ministerId, rulesSignature],
    queryFn: () =>
      eventParticipantsRepository.calculateParticipantPrice(
        eventId,
        basePrice,
        type === 'MINISTER' ? ministerId : undefined,
        undefined,
        new Date(),
        undefined,
        { participantType: type }
      ),
    enabled: ready,
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FlaskConical className="size-4" /> Probar las reglas
        </CardTitle>
        <CardDescription>
          Elige un participante y mira qué regla gana y cuánto pagaría si se inscribiera hoy. No guarda nada.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <SegmentedControl
          options={PARTICIPANT_TYPE_OPTIONS}
          value={type}
          onChange={(v) => {
            setType(v);
            setMinisterId('');
          }}
        />

        {type === 'MINISTER' ? (
          <MinisterSearchCombobox eventId={eventId} value={ministerId} onSelect={setMinisterId} />
        ) : (
          <p className="text-xs text-muted-foreground">
            Solo se evalúan las reglas que no dependen de datos de ministro.
          </p>
        )}

        {ready && (
          <div className="rounded-lg border bg-muted/50 p-3 text-sm">
            {isFetching || !result ? (
              <span className="text-muted-foreground">Calculando…</span>
            ) : (
              <div className="space-y-1">
                <p>
                  Regla aplicada:{' '}
                  <strong>{result.applied_rule ? result.applied_rule.name : 'ninguna (precio base)'}</strong>
                </p>
                <p>
                  Pagaría: <strong className="text-lg">{formatBs(result.final_price)}</strong>
                  {result.discount_amount > 0 && (
                    <span className="ml-2 text-green-600">
                      (ahorra {formatBs(result.discount_amount)} · {result.discount_percentage.toFixed(1)}%)
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
